// As peças da Ficha da OS divididas por categoria, cada uma recolhível, com
// o resumo no cabeçalho (protótipo escolhido pelo dono: variação C, branch
// `prototipo/ficha-por-categoria`). As linhas são as mesmas de
// `_ficha-tipo-peca.ts` — aqui só a organização em volta. Padrão em
// docs/design/ui.md, "Grupo recolhível".

import type { Id } from "../../domain/ids.ts";
import { botao, el } from "../ui.ts";
import { casaBusca } from "./_cadastro.ts";
import type { LinhaTipoPeca } from "./_ficha-tipo-peca.ts";
import {
  abreNaMontagem,
  agruparPorCategoria,
  resumirCategoria,
  situacaoCategoria,
  temPeca,
  type Impedimento,
  type ResumoCategoria,
} from "./_resumo-categoria.ts";

/** Uma peça da Ficha: o Tipo e a linha que a Ficha já montou para ele. */
export interface PecaDaFicha {
  tipo: { id: Id; nome: string; categoriaId: Id };
  linha: LinhaTipoPeca;
}

export interface OpcoesFichaCategorias {
  pecas: readonly PecaDaFicha[];
  nomeDaCategoria: (id: Id) => string;
  cabecalhoColunas: () => HTMLElement;
  /** Tipos cadastrados para o Cliente (cadastro do Cliente): com a Coleta
   * começando, abrem as categorias que têm algum deles. */
  tiposDoCliente: ReadonlySet<Id>;
  /**
   * Aberta/recolhida escolhida pela pessoa numa OS já lançada, por
   * categoria. Vive fora da Ficha — qualquer mutação do Store remonta a
   * ficha do zero, e a escolha não pode voltar ao padrão a cada "Salvar".
   * `null` sem Lote: a escolha vale só enquanto a ficha estiver montada.
   */
  aberturas: Map<Id, boolean> | null;
}

export interface FichaCategorias {
  readonly element: HTMLElement;
  /** A cada mudança de campo: resumo, situação e impedimento novo. */
  atualizar(): void;
}

interface Categoria {
  categoriaId: Id;
  nome: string;
  pecas: PecaDaFicha[];
  secao: HTMLElement;
  titulo: HTMLButtonElement;
  seta: HTMLElement;
  corpo: HTMLElement;
  numeros: HTMLElement;
  selo: HTMLElement;
  /** Aberta fora da busca: escolha da pessoa, senão o padrão da montagem. */
  aberta: boolean;
  /** Aberta/recolhida à mão durante a busca — some quando a busca muda. */
  abertaNaBusca: boolean | null;
  impedimento: Impedimento | null;
}

function resumoDe(pecas: readonly PecaDaFicha[]): ResumoCategoria {
  return resumirCategoria(pecas.map((p) => p.linha.resumo()));
}

export function fichaCategorias(opcoes: OpcoesFichaCategorias): FichaCategorias {
  const busca = el("input", {
    type: "search",
    placeholder: "Filtrar peças…",
    "aria-label": "Filtrar peças",
  }) as HTMLInputElement;
  const vazio = el("p", { class: "muted mini-vazio" }, "Nenhuma peça com esse filtro.");
  vazio.hidden = true;

  const buscando = (): boolean => busca.value.trim() !== "";
  const casaNaBusca = (p: PecaDaFicha, cat: Categoria): boolean => casaBusca(busca.value, p.tipo.nome, cat.nome);

  const desenhar = (): void => {
    let visiveis = 0;
    for (const cat of categorias) {
      let casam = 0;
      for (const p of cat.pecas) {
        const ok = casaNaBusca(p, cat);
        p.linha.element.hidden = !ok;
        if (ok) casam++;
      }
      cat.secao.hidden = casam === 0;
      visiveis += casam;
      const abrir = buscando() ? (cat.abertaNaBusca ?? casam > 0) : cat.aberta;
      cat.corpo.hidden = !abrir;
      cat.titulo.setAttribute("aria-expanded", String(abrir));
      cat.seta.textContent = abrir ? "▴" : "▾";
    }
    vazio.hidden = visiveis > 0;
  };

  /** A escolha da pessoa: durante a busca vale só para a busca. */
  const escolher = (cat: Categoria, abrir: boolean): void => {
    if (buscando()) {
      cat.abertaNaBusca = abrir;
      return;
    }
    cat.aberta = abrir;
    opcoes.aberturas?.set(cat.categoriaId, abrir);
  };

  const grupos = agruparPorCategoria(
    opcoes.pecas.map((p) => p.tipo),
    opcoes.nomeDaCategoria,
  );
  // Coleta começando: nenhuma peça lançada na OS ainda
  const coletaComecando = grupos.every(
    (g) => !temPeca(resumoDe(g.indices.map((i) => opcoes.pecas[i]!))),
  );
  const categorias: Categoria[] = grupos.map((grupo) => {
    const pecas = grupo.indices.map((i) => opcoes.pecas[i]!);
    const id = crypto.randomUUID();
    const idCorpo = `ficha-categoria-corpo-${id}`;
    const idNumeros = `ficha-categoria-numeros-${id}`;
    const idSelo = `ficha-categoria-selo-${id}`;
    // botão com cara de título (mesmo recurso do `th-rotulo`): alvo de
    // teclado e de leitor de tela; o clique sobe para o cabeçalho
    const titulo = el(
      "button",
      {
        type: "button",
        class: "ficha-categoria-titulo",
        "aria-expanded": "false",
        "aria-controls": idCorpo,
        "aria-describedby": `${idNumeros} ${idSelo}`,
      },
      grupo.nome,
    ) as HTMLButtonElement;
    const seta = el("span", { class: "ficha-categoria-seta", "aria-hidden": "true" }, "▾");
    const numeros = el("span", { class: "ficha-categoria-numeros", id: idNumeros });
    const selo = el("span", { class: "pill-categoria", id: idSelo });
    const cabecalho = el(
      "div",
      { class: "ficha-categoria-cab" },
      el("h4", {}, titulo, el("small", {}, `${pecas.length} ${pecas.length === 1 ? "peça" : "peças"}`)),
      numeros,
      selo,
      seta,
    );
    const corpo = el(
      "div",
      { class: "ficha-categoria-corpo", id: idCorpo },
      opcoes.cabecalhoColunas(),
      ...pecas.map((p) => p.linha.element),
    );
    const resumo = resumoDe(pecas);
    const cat: Categoria = {
      categoriaId: grupo.categoriaId,
      nome: grupo.nome,
      pecas,
      secao: el("section", { class: "ficha-categoria" }, cabecalho, corpo),
      titulo,
      seta,
      corpo,
      numeros,
      selo,
      // o padrão é decidido UMA vez, na montagem — recalcular a cada tecla
      // recolheria a categoria debaixo do campo que a pessoa está usando
      aberta:
        opcoes.aberturas?.get(grupo.categoriaId) ??
        abreNaMontagem(resumo, {
          coletaComecando,
          temPecaDoCliente: pecas.some((p) => opcoes.tiposDoCliente.has(p.tipo.id)),
        }),
      abertaNaBusca: null,
      impedimento: resumo.impedimento,
    };
    cabecalho.addEventListener("click", () => {
      escolher(cat, cat.corpo.hidden);
      desenhar();
    });
    return cat;
  });

  const definirTodas = (abrir: boolean) => (): void => {
    for (const cat of categorias) escolher(cat, abrir);
    desenhar();
  };
  const abrirTodas = botao("link", {}, "Abrir Todas");
  const recolherTodas = botao("link", {}, "Recolher Todas");
  abrirTodas.addEventListener("click", definirTodas(true));
  recolherTodas.addEventListener("click", definirTodas(false));
  busca.addEventListener("input", () => {
    for (const cat of categorias) cat.abertaNaBusca = null;
    desenhar();
  });

  const atualizar = (): void => {
    for (const cat of categorias) {
      const resumo = resumoDe(cat.pecas);
      cat.numeros.textContent = `Recebidas ${resumo.recebida} · devolvidas ${resumo.devolvida} · retidas ${resumo.retida}`;
      const situacao = situacaoCategoria(resumo);
      cat.selo.textContent = situacao.texto;
      cat.selo.className = `pill-categoria pill-categoria-${situacao.tom}`;
      // impedimento que APARECE abre a categoria (uma vez); a pessoa pode
      // recolher de novo — não trava aberta
      if (resumo.impedimento && resumo.impedimento !== cat.impedimento) {
        cat.aberta = true;
        cat.abertaNaBusca = buscando() ? true : cat.abertaNaBusca;
      }
      cat.impedimento = resumo.impedimento;
    }
    desenhar();
  };

  return {
    element: el(
      "div",
      {},
      el(
        "div",
        { class: "contagem-filtros ficha-categorias-barra" },
        el("label", { class: "filtro contagem-busca" }, "Peça", busca),
        el("div", { class: "ficha-categorias-acoes" }, abrirTodas, recolherTodas),
      ),
      el("div", { class: "ficha-categorias" }, ...categorias.map((c) => c.secao)),
      vazio,
    ),
    atualizar,
  };
}
